import { useState, type ReactNode } from "react";
import {
  AlertTriangle,
  Briefcase,
  ChevronDown,
  ChevronUp,
  Globe2,
  Map,
  MapPin,
  Target,
  type LucideIcon,
} from "lucide-react";

import { industryIcon } from "@/lib/buyboxIcons";
import type { describeBuyerProfile } from "@/lib/buyerProfile";

type BuyBoxView = ReturnType<typeof describeBuyerProfile>;

/** How many chips a group shows before "Show all". Two to three rows at the
 *  panel's usual width — enough to read the shape of a Buy Box at a glance
 *  without a buyer targeting 150 cities turning the card into a wall. */
const COLLAPSED_LIMIT = 12;

/**
 * A wrapped list collapsed to its first `limit` items, with a Show all / Show
 * less toggle. Generic so the Buy Box editor's "Targeting" chips can share it.
 */
export function ShowMoreList<T>({
  items,
  render,
  limit = COLLAPSED_LIMIT,
  className = "flex flex-wrap gap-1.5",
}: {
  items: T[];
  render: (item: T, index: number) => ReactNode;
  limit?: number;
  className?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const overflow = items.length > limit;
  const shown = expanded || !overflow ? items : items.slice(0, limit);
  return (
    <div>
      <div className={className}>{shown.map(render)}</div>
      {overflow && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="mt-2 inline-flex items-center gap-1 rounded-md text-xs font-bold text-primary transition-colors hover:text-primary/80"
        >
          {expanded ? (
            <>
              Show less <ChevronUp className="h-3.5 w-3.5" />
            </>
          ) : (
            <>
              Show all {items.length.toLocaleString()} <ChevronDown className="h-3.5 w-3.5" />
            </>
          )}
        </button>
      )}
    </div>
  );
}

function Chip({ icon: Icon, children }: { icon?: LucideIcon; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/8 px-2.5 py-1 text-xs font-semibold text-foreground ring-1 ring-primary/20">
      {Icon && <Icon className="h-3 w-3 shrink-0 text-primary" />}
      {children}
    </span>
  );
}

function ChipGroup({
  icon: Icon,
  label,
  items,
  itemIcon,
}: {
  icon: LucideIcon;
  label: string;
  items: string[];
  /** Per-chip icon, e.g. each industry's own glyph. */
  itemIcon?: (item: string) => LucideIcon;
}) {
  return (
    <div className="min-w-0">
      <div className="mb-2 flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5 text-primary" />
        <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">{label}</span>
        {items.length > 0 && (
          <span className="rounded-full bg-muted px-1.5 py-px text-[0.65rem] font-bold tabular-nums text-muted-foreground">
            {items.length.toLocaleString()}
          </span>
        )}
      </div>
      {items.length > 0 ? (
        <ShowMoreList items={items} render={(item, i) => <Chip key={`${item}-${i}`} icon={itemIcon?.(item)}>{item}</Chip>} />
      ) : (
        <span className="text-sm text-muted-foreground/70">Not set</span>
      )}
    </div>
  );
}

function StatTile({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="min-w-0 rounded-xl border border-border/70 bg-background/50 px-3 py-2.5 sm:px-4 sm:py-3">
      <div className="text-[0.6rem] font-bold uppercase leading-tight tracking-[0.1em] text-muted-foreground sm:text-[0.65rem] sm:tracking-[0.12em]">
        {label}
      </div>
      <div className="mt-1 font-display text-sm font-semibold leading-tight text-foreground sm:text-lg">
        {value ?? <span className="font-sans text-xs font-normal text-muted-foreground/70 sm:text-sm">Not set</span>}
      </div>
    </div>
  );
}

/**
 * The Buy Box — the buyer's acquisition target, and the first thing every view
 * of a buyer shows. Rendered by the buyer's own My Profile card and the staff
 * buyer-profile modal (admin + BL Advisor), so all three read identically.
 *
 * Each list is its own field (industries, states, cities) and collapses past
 * COLLAPSED_LIMIT: a buyer can target hundreds of cities, which used to print
 * as one unbroken comma string plus a second, duplicate wall of tag chips.
 */
export default function BuyBoxPanel({
  view,
  subtitle,
  sdeConflict,
  action,
}: {
  view: BuyBoxView;
  subtitle: string;
  /** Shown when the minimum SDE sits above the whole budget. */
  sdeConflict: ReactNode;
  /** Optional header control, e.g. the buyer's own Edit button. */
  action?: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-2xl border border-primary/25 bg-card shadow-sm">
      <div className="flex items-start justify-between gap-3 border-b border-primary/15 bg-primary/5 px-5 py-4 sm:px-6">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gold-gradient text-primary-foreground shadow-sm">
            <Target className="h-4.5 w-4.5" />
          </div>
          <div className="min-w-0">
            <h3 className="heading-bold m-0 text-lg leading-tight text-foreground">Buy Box</h3>
            <p className="text-xs text-muted-foreground">{subtitle}</p>
          </div>
        </div>
        {action}
      </div>

      <div className="space-y-6 p-5 sm:p-6">
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          <StatTile label="Budget" value={view.budgetLabel} />
          <StatTile label="Minimum SDE" value={view.sde} />
          <StatTile label="Years in business" value={view.years} />
        </div>

        {view.sdeConflict && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3.5 py-2.5 text-xs font-medium text-amber-600">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{sdeConflict}</span>
          </div>
        )}

        <ChipGroup icon={Briefcase} label="Industries" items={view.industries} itemIcon={industryIcon} />

        {view.legacyLocations.length > 0 ? (
          <ChipGroup icon={MapPin} label="Locations" items={view.legacyLocations} />
        ) : view.countries.length > 0 ? (
          <ChipGroup icon={Globe2} label="Countries" items={view.countries} />
        ) : (
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
            <ChipGroup icon={Map} label="States" items={view.states} />
            <ChipGroup icon={MapPin} label="Cities" items={view.cities} />
          </div>
        )}
      </div>
    </section>
  );
}
